import {
  aiAllowanceMessage,
  aiAllowanceState,
  aiRequestsLeft,
  currentAIUsage,
  resetPhrase,
  type AIAllowanceState,
} from '../../shared/aiAllowance';
import { useSession, useSettings } from '../api/hooks';

export interface AIAllowance {
  /** Settings have loaded and AI is on for this person. When it's off, src/lib/aiOff.ts says why. */
  enabled: boolean;
  state: AIAllowanceState;
  /** Requests left today under the personal cap; null when only the server-wide cap applies. */
  left: number | null;
  /** AI is on, but nothing can run until the reset: none left, or the server-wide cap is reached. */
  exhausted: boolean;
  /** An AI action can run now. */
  canUse: boolean;
  /** When counts reset, in the profile's time zone: "at 7:00 PM", "tomorrow at 4:00 PM" or "at midnight". */
  resetPhrase: string;
  /** "8 AI requests left today", or when AI actions come back. Null when there's nothing to say. */
  message: string | null;
}

const UNKNOWN: AIAllowance = { enabled: false, state: 'ok', left: null, exhausted: false, canUse: false, resetPhrase: '', message: null };

/**
 * Today's AI allowance as AI actions need it. The server still enforces every
 * limit; this lets the app say so before a tap instead of after a failed request.
 * Counts refresh after each AI call (refreshAIUsage) and at the reset (useSettings).
 */
export function useAIAllowance(): AIAllowance {
  const settings = useSettings().data;
  // Without sign-in the person reading runs the server, so the server-wide cap is theirs to raise.
  const ownServer = useSession().data?.required === false;
  if (!settings) return UNKNOWN;
  // Past the reset, yesterday's counts no longer apply (a tab that slept through midnight UTC).
  const now = new Date();
  const usage = currentAIUsage(settings.aiUsage, now);
  const enabled = settings.ai.enabled;
  const state = aiAllowanceState(usage);
  const left = aiRequestsLeft(usage);
  const phrase = resetPhrase(usage.resetsAt, settings.profile.timezone, now);
  const exhausted = enabled && (state === 'out' || state === 'busy');
  return {
    enabled,
    state,
    left,
    exhausted,
    canUse: enabled && !exhausted,
    resetPhrase: phrase,
    message: enabled ? aiAllowanceMessage(state, left, phrase, ownServer) : null,
  };
}
